import { getApiUrl } from "@/utils/api";
import { useState, useEffect, useCallback } from "react";
import { Button } from "@/components/ui/button";
import {
  Eye, Users, Mail, Phone, MessageCircle, Clock, TrendingUp, BarChart3,
  Globe, Megaphone, MousePointer, User, Loader2, RefreshCw, ArrowUp, ArrowDown
} from "lucide-react";
import {
  BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid,
  Tooltip, ResponsiveContainer, Area, AreaChart
} from "recharts";
import axios from "axios";
import { toast } from "sonner";

const API = getApiUrl();

const PERIOD_OPTIONS = [
  { value: "7d", label: "7 Days" },
  { value: "30d", label: "30 Days" },
  { value: "90d", label: "90 Days" },
];

const SUB_TABS = [
  { id: "overview", label: "Overview", icon: BarChart3 },
  { id: "views", label: "Views", icon: Eye },
  { id: "enquiries", label: "Enquiries", icon: Mail },
  { id: "calls", label: "Calls", icon: Phone },
  { id: "sources", label: "Sources", icon: Globe },
];

function StatCard({ icon: Icon, label, value, sub, color }) {
  const colors = {
    blue: "from-blue-50 to-blue-100 border-blue-200",
    green: "from-green-50 to-green-100 border-green-200",
    purple: "from-purple-50 to-purple-100 border-purple-200",
    amber: "from-amber-50 to-amber-100 border-amber-200",
  };
  const iconColors = { blue: "bg-blue-500", green: "bg-green-500", purple: "bg-purple-500", amber: "bg-amber-500" };
  const textColors = { blue: "text-blue-900", green: "text-green-900", purple: "text-purple-900", amber: "text-amber-900" };
  const subColors = { blue: "text-blue-600", green: "text-green-600", purple: "text-purple-600", amber: "text-amber-600" };

  return (
    <div className={`bg-gradient-to-br ${colors[color]} border p-4 rounded-lg`}>
      <div className="flex items-center gap-2 mb-2">
        <div className={`p-1.5 ${iconColors[color]} rounded-lg`}>
          <Icon className="h-4 w-4 text-white" />
        </div>
        <span className={`text-xs font-medium ${subColors[color]}`}>{label}</span>
      </div>
      <p className={`text-2xl font-bold ${textColors[color]}`}>{typeof value === "number" ? value.toLocaleString() : value}</p>
      {sub && <p className={`text-xs ${subColors[color]} mt-0.5`}>{sub}</p>}
    </div>
  );
}

function ChartTooltip({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-white border border-border shadow-lg p-2 rounded text-xs">
      <p className="font-medium mb-1">{label}</p>
      {payload.map((p, i) => (
        <p key={i} style={{ color: p.color }}>
          {p.name}: <strong>{p.value}</strong>
        </p>
      ))}
    </div>
  );
}

function formatDate(dateStr) {
  const d = new Date(dateStr + "T00:00:00");
  return d.toLocaleDateString("en-GB", { day: "numeric", month: "short" });
}

export default function StatisticsTab({ enquiries = [] }) {
  const [subTab, setSubTab] = useState("overview");
  const [period, setPeriod] = useState("30d");
  const [stats, setStats] = useState(null);
  const [history, setHistory] = useState(null);
  const [loading, setLoading] = useState(false);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [statsRes, histRes] = await Promise.all([
        axios.get(`${API}/property-admin/statistics`, { withCredentials: true }),
        axios.get(`${API}/property-admin/statistics/history?period=${period}`, { withCredentials: true }),
      ]);
      setStats(statsRes.data);
      setHistory(histRes.data);
    } catch (e) {
      console.error("Stats fetch error:", e);
      toast.error("Failed to load statistics");
    } finally {
      setLoading(false);
    }
  }, [period]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  // Thin out chart labels for readability
  const chartData = (history?.chart_data || []).map((d) => ({
    ...d,
    label: formatDate(d.date),
  }));

  const tickInterval = chartData.length > 14 ? Math.floor(chartData.length / 7) : 0;

  const periodTotals = history?.period_totals || { views: 0, calls: 0, enquiries: 0 };

  return (
    <div className="bg-white border border-border p-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between border-b border-border pb-4 mb-5 gap-3">
        <div>
          <h3 className="font-serif text-xl font-medium">Home Analytics</h3>
          <p className="text-sm text-muted-foreground mt-0.5">Performance metrics & historic data</p>
        </div>
        <Button variant="outline" size="sm" className="rounded-none" onClick={fetchAll} disabled={loading}>
          {loading ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <RefreshCw className="h-3.5 w-3.5 mr-1" />}
          Refresh
        </Button>
      </div>

      {/* Sub-navigation */}
      <div className="flex flex-wrap gap-1 mb-5 border-b border-border">
        {SUB_TABS.map((t) => (
          <button
            key={t.id}
            onClick={() => setSubTab(t.id)}
            className={`flex items-center gap-1.5 px-3 py-2 text-xs font-medium border-b-2 transition-colors ${
              subTab === t.id
                ? "border-primary text-primary"
                : "border-transparent text-muted-foreground hover:text-foreground"
            }`}
          >
            <t.icon className="h-3.5 w-3.5" />
            {t.label}
          </button>
        ))}
      </div>

      {/* Period Toggle (for non-overview tabs) */}
      {subTab !== "overview" && (
        <div className="flex gap-1 mb-5">
          {PERIOD_OPTIONS.map((p) => (
            <button
              key={p.value}
              onClick={() => setPeriod(p.value)}
              className={`px-3 py-1.5 text-xs font-medium rounded transition-colors ${
                period === p.value
                  ? "bg-primary text-primary-foreground"
                  : "bg-muted text-muted-foreground hover:bg-muted/80"
              }`}
            >
              {p.label}
            </button>
          ))}
        </div>
      )}

      {loading && !stats ? (
        <div className="flex items-center justify-center py-16">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      ) : (
        <>
          {/* === OVERVIEW === */}
          {subTab === "overview" && stats && (
            <div className="space-y-6">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <StatCard icon={Eye} label="Total Views" value={stats.total_views || 0} sub="All time" color="blue" />
                <StatCard icon={Users} label="Unique Visitors" value={stats.unique_visitors || 0} sub="Individual IPs" color="green" />
                <StatCard icon={Mail} label="Enquiries" value={stats.total_enquiries || enquiries.length || 0} sub="Total received" color="purple" />
                <StatCard icon={Phone} label="Phone Calls" value={stats.total_calls || 0} sub="Call clicks" color="amber" />
              </div>

              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <StatCard icon={MessageCircle} label="WhatsApp Chats" value={stats.total_whatsapp || 0} sub="WhatsApp CTA clicks" color="green" />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <div className="bg-muted/30 border border-border p-4 rounded-lg">
                  <div className="flex items-center gap-2 mb-1">
                    <Clock className="h-4 w-4 text-muted-foreground" />
                    <span className="text-sm font-medium">Avg. Time on Page</span>
                  </div>
                  <p className="text-xl font-bold">
                    {stats.avg_time_on_page ? `${Math.floor(stats.avg_time_on_page / 60)}m ${stats.avg_time_on_page % 60}s` : "—"}
                  </p>
                </div>
                <div className="bg-muted/30 border border-border p-4 rounded-lg">
                  <div className="flex items-center gap-2 mb-1">
                    <TrendingUp className="h-4 w-4 text-muted-foreground" />
                    <span className="text-sm font-medium">Conversion Rate</span>
                  </div>
                  <p className="text-xl font-bold">
                    {stats.conversion_rate ? `${stats.conversion_rate.toFixed(1)}%` : "0%"}
                  </p>
                  <p className="text-xs text-muted-foreground">Visitors → Enquiries</p>
                </div>
                <div className="bg-muted/30 border border-border p-4 rounded-lg">
                  <div className="flex items-center gap-2 mb-1">
                    <MousePointer className="h-4 w-4 text-muted-foreground" />
                    <span className="text-sm font-medium">This Month</span>
                  </div>
                  <p className="text-xl font-bold">{stats.views_this_month || 0} views</p>
                  <p className="text-xs text-muted-foreground">{stats.enquiries_this_month || 0} enquiries</p>
                </div>
              </div>

              {/* Quick weekly chart */}
              <div className="bg-muted/30 border border-border p-4 rounded-lg">
                <h4 className="font-medium mb-3 text-sm flex items-center gap-2">
                  <BarChart3 className="h-4 w-4" /> Views This Week
                </h4>
                <div className="flex items-end justify-between gap-2 h-28">
                  {(stats.views_this_week?.length > 0
                    ? stats.views_this_week
                    : ["Mon","Tue","Wed","Thu","Fri","Sat","Sun"].map(d => ({ day: d, views: 0 }))
                  ).map((day, i) => {
                    const max = Math.max(...(stats.views_this_week?.map(d => d.views) || [1]), 1);
                    const h = day.views > 0 ? Math.max((day.views / max) * 100, 10) : 5;
                    return (
                      <div key={i} className="flex-1 flex flex-col items-center gap-1">
                        <div className="w-full bg-primary/80 rounded-t hover:bg-primary transition-all" style={{ height: `${h}%`, minHeight: "4px" }} title={`${day.views} views`} />
                        <span className="text-[10px] text-muted-foreground">{day.day}</span>
                        <span className="text-[10px] font-medium">{day.views}</span>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Recent enquiries */}
              <div className="bg-muted/30 border border-border p-4 rounded-lg">
                <h4 className="font-medium mb-3 text-sm">Recent Enquiries</h4>
                {enquiries.length > 0 ? (
                  <div className="space-y-2">
                    {enquiries.slice(0, 5).map((eq, i) => (
                      <div key={i} className="flex items-center justify-between py-1.5 border-b border-border last:border-0">
                        <div className="flex items-center gap-2">
                          <div className="w-7 h-7 rounded-full bg-primary/10 flex items-center justify-center">
                            <User className="h-3.5 w-3.5 text-primary" />
                          </div>
                          <div>
                            <p className="font-medium text-xs">{eq.name}</p>
                            <p className="text-[10px] text-muted-foreground">{eq.email}</p>
                          </div>
                        </div>
                        <span className="text-[10px] text-muted-foreground">
                          {eq.created_at ? new Date(eq.created_at).toLocaleDateString() : "Recently"}
                        </span>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-muted-foreground text-center py-4">No enquiries yet</p>
                )}
              </div>
            </div>
          )}

          {/* === VIEWS TAB === */}
          {subTab === "views" && (
            <div className="space-y-5">
              <div className="grid grid-cols-3 gap-3">
                <StatCard icon={Eye} label={`Views (${period})`} value={periodTotals.views} color="blue" />
                <StatCard icon={Users} label="Unique Visitors" value={stats?.unique_visitors || 0} sub="All time" color="green" />
                <StatCard icon={Clock} label="Avg. Time" value={stats?.avg_time_on_page ? `${Math.floor(stats.avg_time_on_page / 60)}m ${stats.avg_time_on_page % 60}s` : "—"} color="amber" />
              </div>

              <div className="bg-muted/30 border border-border p-4 rounded-lg">
                <h4 className="font-medium mb-3 text-sm">Daily Views</h4>
                <ResponsiveContainer width="100%" height={250}>
                  <AreaChart data={chartData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                    <XAxis dataKey="label" tick={{ fontSize: 10 }} interval={tickInterval} />
                    <YAxis allowDecimals={false} tick={{ fontSize: 10 }} />
                    <Tooltip content={<ChartTooltip />} />
                    <Area type="monotone" dataKey="views" name="Views" stroke="#3b82f6" fill="#3b82f6" fillOpacity={0.15} strokeWidth={2} />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            </div>
          )}

          {/* === ENQUIRIES TAB === */}
          {subTab === "enquiries" && (
            <div className="space-y-5">
              <div className="grid grid-cols-3 gap-3">
                <StatCard icon={Mail} label={`Enquiries (${period})`} value={periodTotals.enquiries} color="purple" />
                <StatCard icon={Mail} label="Total Enquiries" value={stats?.total_enquiries || enquiries.length} sub="All time" color="blue" />
                <StatCard icon={TrendingUp} label="Conversion" value={stats?.conversion_rate ? `${stats.conversion_rate.toFixed(1)}%` : "0%"} color="green" />
              </div>

              <div className="bg-muted/30 border border-border p-4 rounded-lg">
                <h4 className="font-medium mb-3 text-sm">Daily Enquiries</h4>
                <ResponsiveContainer width="100%" height={250}>
                  <BarChart data={chartData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                    <XAxis dataKey="label" tick={{ fontSize: 10 }} interval={tickInterval} />
                    <YAxis allowDecimals={false} tick={{ fontSize: 10 }} />
                    <Tooltip content={<ChartTooltip />} />
                    <Bar dataKey="enquiries" name="Enquiries" fill="#8b5cf6" radius={[3, 3, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>

              {/* Recent enquiries list */}
              <div className="bg-muted/30 border border-border p-4 rounded-lg">
                <h4 className="font-medium mb-3 text-sm">Recent Enquiries</h4>
                {enquiries.length > 0 ? (
                  <div className="space-y-2">
                    {enquiries.slice(0, 8).map((eq, i) => (
                      <div key={i} className="flex items-center justify-between py-1.5 border-b border-border last:border-0">
                        <div className="flex items-center gap-2">
                          <div className="w-7 h-7 rounded-full bg-purple-100 flex items-center justify-center">
                            <User className="h-3.5 w-3.5 text-purple-600" />
                          </div>
                          <div>
                            <p className="font-medium text-xs">{eq.name}</p>
                            <p className="text-[10px] text-muted-foreground">{eq.email} {eq.phone ? `· ${eq.phone}` : ""}</p>
                          </div>
                        </div>
                        <span className="text-[10px] text-muted-foreground">
                          {eq.created_at ? new Date(eq.created_at).toLocaleDateString() : "Recently"}
                        </span>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-muted-foreground text-center py-4">No enquiries yet</p>
                )}
              </div>
            </div>
          )}

          {/* === CALLS TAB === */}
          {subTab === "calls" && (
            <div className="space-y-5">
              <div className="grid grid-cols-2 gap-3">
                <StatCard icon={Phone} label={`Calls (${period})`} value={periodTotals.calls} color="amber" />
                <StatCard icon={Phone} label="Total Calls" value={stats?.total_calls || 0} sub="All time" color="blue" />
              </div>

              <div className="bg-muted/30 border border-border p-4 rounded-lg">
                <h4 className="font-medium mb-3 text-sm">Daily Call Clicks</h4>
                <ResponsiveContainer width="100%" height={250}>
                  <BarChart data={chartData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                    <XAxis dataKey="label" tick={{ fontSize: 10 }} interval={tickInterval} />
                    <YAxis allowDecimals={false} tick={{ fontSize: 10 }} />
                    <Tooltip content={<ChartTooltip />} />
                    <Bar dataKey="calls" name="Calls" fill="#f59e0b" radius={[3, 3, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
          )}

          {/* === SOURCES TAB === */}
          {subTab === "sources" && (
            <div className="space-y-5">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Referrers */}
                <div className="bg-muted/30 border border-border p-4 rounded-lg">
                  <h4 className="font-medium mb-3 text-sm flex items-center gap-2">
                    <Globe className="h-4 w-4" /> Top Referrers
                  </h4>
                  {history?.referrers?.length > 0 ? (
                    <div className="space-y-2">
                      {history.referrers.map((r, i) => (
                        <div key={i} className="flex items-center justify-between">
                          <span className="text-xs truncate max-w-[200px]">{r.domain}</span>
                          <span className="text-xs font-medium bg-blue-100 text-blue-700 px-2 py-0.5 rounded">{r.count}</span>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-sm text-muted-foreground text-center py-6">No referrer data yet</p>
                  )}
                </div>

                {/* Campaigns */}
                <div className="bg-muted/30 border border-border p-4 rounded-lg">
                  <h4 className="font-medium mb-3 text-sm flex items-center gap-2">
                    <Megaphone className="h-4 w-4" /> Campaigns
                  </h4>
                  {history?.campaigns?.length > 0 ? (
                    <div className="space-y-2">
                      {history.campaigns.map((c, i) => (
                        <div key={i} className="flex items-center justify-between">
                          <span className="text-xs truncate max-w-[200px]">{c.name}</span>
                          <span className="text-xs font-medium bg-purple-100 text-purple-700 px-2 py-0.5 rounded">{c.count}</span>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-sm text-muted-foreground text-center py-6">No campaign data yet. Use <code className="text-[10px] bg-muted p-0.5 rounded">?utm_campaign=name</code> in URLs</p>
                  )}
                </div>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
